<template>
  <q-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    persistent
    transition-show="scale"
    transition-hide="scale"
  >
    <q-card class="ca-validation-modal" style="min-width: 500px">
      <q-card-section class="row items-center q-pb-none">
        <div class="text-h6 text-brand-5">
          <q-icon name="verified" class="q-mr-sm" />
          Validação de CA
          <span v-if="caNumber" class="text-primary"> - {{ caNumber }} </span>
        </div>
        <q-space />
        <q-btn icon="close" flat round dense @click="closeModal" />
      </q-card-section>

      <q-card-section v-if="loading" class="text-center">
        <q-spinner color="primary" size="3em" />
        <div class="q-mt-md">Validando CA...</div>
      </q-card-section>

      <q-card-section v-else-if="validationData">
        <div class="q-mb-md">
          <div class="text-subtitle2 text-brand-5 q-mb-sm">
            <q-icon name="info" class="q-mr-xs" />
            Status:
          </div>
          <div class="row items-center">
            <span
              :class="getStatusClass(validationData.status || 'Atualizado')"
              class="elipse q-mr-sm"
            />
            <span
              class="text-body1 text-weight-medium"
              :class="getStatusTextClass(validationData.status || 'Atualizado')"
            >
              {{ validationData.status || "Atualizado" }}
            </span>
          </div>
        </div>

        <div class="q-mb-md">
          <div class="text-subtitle2 text-brand-5 q-mb-sm">
            <q-icon name="event" class="q-mr-xs" />
            Validade:
          </div>
          <div class="text-body1 text-weight-medium">
            {{ formatDate(validationData.expires_in) }}
          </div>
        </div>

        <div class="q-mb-md">
          <div class="text-subtitle2 text-brand-5 q-mb-sm">
            <q-icon name="description" class="q-mr-xs" />
            Informações:
          </div>
          <div class="text-body2">CA validado e atualizado com sucesso</div>
        </div>

        <q-separator class="q-my-md" />

        <div class="text-caption text-grey-6">
          Validação realizada em: {{ formatDateTime(new Date()) }}
        </div>
      </q-card-section>

      <q-card-section v-else-if="error">
        <div class="text-center">
          <q-icon name="error" color="negative" size="3em" />
          <div class="text-h6 text-negative q-mt-md">Erro na Validação</div>
          <div class="text-body2 q-mt-sm">{{ error }}</div>
        </div>
      </q-card-section>

      <q-card-actions align="right" v-if="!loading">
        <q-btn flat label="Cancelar" color="grey" @click="closeModal" />
        <q-btn
          v-if="validationData && !error"
          :loading="updating"
          label="Atualizar Validade"
          color="positive"
          @click="updateCaValidity"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
import { ref, watch } from "vue";
import { useQuasar } from "quasar";
import caValidationService from "../services/caValidationService";

export default {
  name: "CaValidationModal",
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    caNumber: {
      type: String,
      required: true,
    },
    caId: {
      type: [Number, String],
      default: null,
    },
  },
  emits: ["update:modelValue", "ca-updated"],
  setup(props, { emit }) {
    const quasar = useQuasar();

    const loading = ref(false);
    const updating = ref(false);
    const validationData = ref(null);
    const error = ref(null);

    const validateCa = async () => {
      if (!props.caNumber) return;

      loading.value = true;
      error.value = null;
      validationData.value = null;

      try {
        const result = await caValidationService.validateCa(props.caNumber);

        if (result.success) {
          validationData.value = result.data;
        } else {
          error.value = result.error;
        }
      } catch (err) {
        error.value = "Erro inesperado ao validar CA";
      } finally {
        loading.value = false;
      }
    };

    const updateCaValidity = async () => {
      if (!props.caId || !validationData.value) {
        quasar.notify({
          message: "Dados insuficientes para atualização",
          color: "negative",
        });
        return;
      }

      updating.value = true;

      try {
        const result = await caValidationService.updateCaValidity(
          props.caId,
          validationData.value
        );

        if (result.success) {
          quasar.notify({
            message: "CA atualizado com sucesso",
            color: "positive",
          });
          emit("ca-updated", result.data);
          closeModal();
        } else {
          quasar.notify({
            message: result.error || "Erro ao atualizar CA",
            color: "negative",
          });
        }
      } catch (err) {
        quasar.notify({
          message: "Erro inesperado ao atualizar CA",
          color: "negative",
        });
      } finally {
        updating.value = false;
      }
    };

    const closeModal = () => {
      emit("update:modelValue", false);
      // Reset dados quando fechar
      setTimeout(() => {
        validationData.value = null;
        error.value = null;
        loading.value = false;
        updating.value = false;
      }, 300);
    };

    const getStatusClass = (status) => {
      const statusLower = status?.toLowerCase();
      if (statusLower === "válido" || statusLower === "valid") {
        return "bg-positive";
      } else if (statusLower === "vencido" || statusLower === "expired") {
        return "bg-negative";
      } else if (statusLower === "vencendo" || statusLower === "expiring") {
        return "bg-warning";
      }
      return "bg-grey";
    };

    const getStatusTextClass = (status) => {
      const statusLower = status?.toLowerCase();
      if (statusLower === "válido" || statusLower === "valid") {
        return "text-positive";
      } else if (statusLower === "vencido" || statusLower === "expired") {
        return "text-negative";
      } else if (statusLower === "vencendo" || statusLower === "expiring") {
        return "text-warning";
      }
      return "text-grey";
    };

    const formatDate = (dateString) => {
      if (!dateString) return "N/A";
      try {
        // Se a data está no formato YYYY-MM-DD, tratamos como data local
        if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = dateString.split("-");
          // Cria a data considerando o fuso horário local (mês -1 porque JavaScript usa 0-11)
          const date = new Date(
            parseInt(year),
            parseInt(month) - 1,
            parseInt(day)
          );
          return date.toLocaleDateString("pt-BR");
        } else {
          // Para outros formatos, usa o comportamento padrão
          const date = new Date(dateString);
          return date.toLocaleDateString("pt-BR");
        }
      } catch {
        return dateString;
      }
    };

    const formatDateTime = (date) => {
      return date.toLocaleString("pt-BR");
    };

    // Observa mudanças no modal para iniciar validação
    watch(
      () => props.modelValue,
      (newValue) => {
        if (newValue && props.caNumber) {
          validateCa();
        }
      }
    );

    return {
      loading,
      updating,
      validationData,
      error,
      updateCaValidity,
      closeModal,
      getStatusClass,
      getStatusTextClass,
      formatDate,
      formatDateTime,
    };
  },
};
</script>

<style scoped>
.ca-validation-modal {
  max-width: 600px;
}

.elipse {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
}

.text-brand-5 {
  color: #1976d2;
}
</style>
